import { LitElement, css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import type { Operator } from '../state/types';
import { i18n } from '../i18n/nl';
import { iconBack, iconChevronDown } from './icons';
import './settings-menu';

export type HeaderMode =
  | 'hero'
  | 'agent'
  | 'assistant'
  | 'intake'
  | 'auth-required'
  | 'ended'
  | 'closed';

/**
 * <embrace-chat-header> — de gradient-header bovenin het paneel.
 *
 * Variant-gestuurd via `mode`:
 *   - hero   : welkomst-greeting, gebruikt als service nog niet gekoppeld is
 *   - agent  : operator-naam + status (actieve conversatie)
 *   - intake : "Start je gesprek" met terug-knop
 *   - ended  : operator + "Gesprek beëindigd"
 *   - closed : "Chat is nu gesloten"
 */
@customElement('embrace-chat-header')
export class EmbraceChatHeader extends LitElement {
  @property({ type: String })
  mode: HeaderMode = 'hero';

  @property({ attribute: false })
  operator: Operator | null = null;

  @property({ type: String })
  orgName = 'Klantenservice';

  @property({ type: String })
  orgSubtitle = '';

  @property({ type: Boolean })
  showBack = false;

  /**
   * Toon het 3-puntjes-actions-menu. Het menu bevat zowel
   * "Download transcript" als — wanneer er een persistente visitor is —
   * de "Mijn gegevens" sectie met wijzig/wis-knoppen.
   */
  @property({ type: Boolean })
  showSettingsMenu = false;

  @property({ attribute: false })
  visitorFirstName = '';

  @property({ attribute: false })
  visitorEmail = '';

  /** Of het menu een "Download transcript"-actie moet tonen. */
  @property({ type: Boolean })
  canDownloadTranscript = false;

  /** Of het menu een "Chatgesprek sluiten"-actie moet tonen. */
  @property({ type: Boolean })
  canCloseConversation = false;

  static override styles = css`
    :host {
      display: block;
    }

    .header {
      padding: 18px 16px 16px 18px;
      position: relative;
      background: var(--ec-header-bg);
      color: var(--ec-on-primary);
    }

    .top-row {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .lead {
      flex: 0 0 auto;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }

    .back, .close {
      appearance: none;
      -webkit-appearance: none;
      background: transparent;
      border: 0;
      color: inherit;
      width: 32px;
      height: 32px;
      border-radius: 8px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      opacity: .7;
      flex: 0 0 auto;
      transition: background var(--ec-duration-fast) var(--ec-ease-smooth),
                  opacity var(--ec-duration-fast) var(--ec-ease-smooth);
    }
    .back:hover, .close:hover {
      background: rgba(255,255,255,.12);
      opacity: 1;
    }
    .back:focus-visible, .close:focus-visible {
      outline: 3px solid rgba(255,255,255,.45);
      outline-offset: 2px;
    }
    .back svg, .close svg { width: 14px; height: 14px; }

    .text {
      flex: 1 1 auto;
      min-width: 0;
    }
    .title {
      font: 700 17px/1.25 var(--ec-font-display);
      overflow-wrap: break-word;
    }
    .subtitle {
      font: 400 13px/1.4 var(--ec-font-family);
      opacity: .75;
      margin-top: 2px;
      overflow-wrap: break-word;
    }
    .spacer { flex: 1; }

    .avatar {
      position: relative;
      flex: 0 0 auto;
    }
    .avatar .photo {
      width: 36px;
      height: 36px;
      border-radius: 9999px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      font: 700 13px/1 var(--ec-font-display);
      border: 2px solid rgba(255,255,255,.18);
    }
    .avatar .presence {
      position: absolute;
      right: -1px;
      bottom: -1px;
      width: 10px;
      height: 10px;
      border-radius: 9999px;
      border: 2px solid #22356A;
    }
    .avatar .presence.online { background: #22C55E; }
    .avatar .presence.offline { background: #D1D1D2; }
  `;

  override render() {
    return html`
      <header class="header">
        <div class="top-row">
          ${this.showBack
            ? html`
                <button class="back" aria-label=${i18n.header.btnBack} @click=${this.onBack}>
                  ${iconBack()}
                </button>
              `
            : nothing}
          ${this.renderLead()}
          <div class="text">${this.renderText()}</div>
          <div class="spacer"></div>
          ${this.showSettingsMenu
            ? html`
                <embrace-settings-menu
                  .firstName=${this.visitorFirstName}
                  .email=${this.visitorEmail}
                  ?canDownloadTranscript=${this.canDownloadTranscript}
                  ?canCloseConversation=${this.canCloseConversation}
                ></embrace-settings-menu>
              `
            : nothing}
          <button
            part="header-close"
            class="close"
            aria-label=${i18n.header.btnClose}
            @click=${this.onClose}
          >
            ${iconChevronDown()}
          </button>
        </div>
      </header>
    `;
  }

  private renderLead() {
    const op = this.operator;
    if (this.mode === 'agent' || this.mode === 'ended') {
      if (op) {
        const presenceClass = this.mode === 'ended' ? 'offline' : 'online';
        return html`
          <div class="avatar">
            <div
              class="photo"
              part="header-avatar"
              style=${`background: ${op.avatarColor || 'var(--ec-accent)'};`}
            >
              ${op.initials}
            </div>
            <div class=${`presence ${presenceClass}`}></div>
          </div>
        `;
      }
    }
    if (this.mode === 'assistant') {
      return html`
        <div class="avatar">
          <div
            class="photo"
            part="header-avatar"
            style="background: var(--ec-assistant-bg); font-size: 18px;"
            aria-hidden="true"
          >🤖</div>
          <div class="presence online"></div>
        </div>
      `;
    }
    return nothing;
  }

  private renderText() {
    const op = this.operator;
    const opName = op?.name ?? i18n.header.fallbackOperator;
    switch (this.mode) {
      case 'agent':
        return html`
          <div class="title" part="header-title">${opName}</div>
          <div class="subtitle">${i18n.header.agentSub}</div>
        `;
      case 'ended':
        return html`
          <div class="title" part="header-title">${opName}</div>
          <div class="subtitle">${i18n.header.endedSub}</div>
        `;
      case 'intake':
        return html`
          <div class="title" part="header-title">${i18n.header.intake}</div>
          <div class="subtitle">${i18n.header.intakeSub}</div>
        `;
      case 'auth-required':
        return html`
          <div class="title" part="header-title">${i18n.header.authRequired}</div>
          <div class="subtitle">${i18n.header.authRequiredSub}</div>
        `;
      case 'assistant':
        return html`
          <div class="title" part="header-title">${i18n.header.assistant}</div>
          <div class="subtitle">${i18n.header.assistantSub}</div>
        `;
      case 'closed':
        return html`
          <div class="title" part="header-title">${i18n.header.closed}</div>
          <div class="subtitle">
            ${this.orgName}${this.orgSubtitle
              ? html` · ${this.orgSubtitle}`
              : nothing}
          </div>
        `;
      case 'hero':
      default:
        return html`
          <div class="title" part="header-title">${i18n.header.hero}</div>
          <div class="subtitle">${i18n.header.heroSub}</div>
        `;
    }
  }


  private onBack() {
    this.dispatchEvent(new CustomEvent('back', { bubbles: true, composed: true }));
  }

  private onClose() {
    this.dispatchEvent(
      new CustomEvent('close-panel', { bubbles: true, composed: true }),
    );
  }

}

declare global {
  interface HTMLElementTagNameMap {
    'embrace-chat-header': EmbraceChatHeader;
  }
}
